import type { ProvenanceTargetType } from 'components/attacks/create/create-attack-types'
import type { CamelCasedProperties, SnakeCasedProperties } from 'type-fest'
import type {
  AttackConfiguration,
  SnakeCaseAttackArgs,
  TargetingStrategy
} from 'types/api/attacks/details/targeting'
import type {
  ExperimentEffect,
  FailureFlagSelector
} from 'types/api/failure-flags/failure-flags'
import type { EvaluationResults } from 'types/api/status-checks/evaluation'
import type {
  EndpointResponse,
  ResponseBodyEvaluation
} from 'types/api/status-checks/status-check'

export enum ScenarioGraphNodeType {
  InfraAttack = 'InfraAttack',
  FailureFlag = 'FailureFlag',
  ContinuousStatusCheck = 'ContinuousStatusCheck',
  SynchronousStatusCheck = 'SynchronousStatusCheck',
  Delay = 'Delay',
  Concurrent = 'Concurrent',
  Continuous = 'Continuous' // forbidden in production
}

export enum ScenarioLifecycle {
  NotStarted = 'NotStarted',
  Active = 'Active',
  HaltRequested = 'HaltRequested',
  Successful = 'Successful',
  Halted = 'Halted',
  Failed = 'Failed',
  UserHalted = 'UserHalted'
}

export interface BaseNodeState {
  lifecycle: ScenarioLifecycle
  startTime?: string // Date
  endTime?: string // Date
  failedReason?: string
  nodeHaltedByGuid?: string
  nodeHaltedById?: string
}

export interface InfraAttackNodeState extends BaseNodeState {
  k8sAttackId?: string
  taskId: string
  taskSummary: {
    clientsMetricsEnabledCount: number
    endTime: string
    stage: string
    totalClients: number
  }
  resolvedTargets: {
    resolvedHosts: string[]
    resolvedContainers: Record<string, string>[]
  }
}

export interface FailureFlagNodeState
  extends SnakeCasedProperties<BaseNodeState> {
  experiment_id: string
  run_summary?: {
    deliveries: number
    impacts: number
    start_time: string
    stop_time: string
  }
}

export interface StatusCheckNodeState extends BaseNodeState {
  privateNetworkInvocationCompleted?: boolean // defaults to false
  privateNetworkInvocationId?: string
  notificationSent?: boolean
  endpointResponse: EndpointResponse
  evaluationResults: EvaluationResults
  lastExecutedAt?: Date
}

export type CamelCaseNodeState = InfraAttackNodeState | StatusCheckNodeState
export type SnakeCaseNodeState = SnakeCasedProperties<CamelCaseNodeState>

export type NodeState = CamelCaseNodeState & SnakeCaseNodeState

export interface BaseGraphNode {
  id?: string
  next?: string
  guid?: string
  type?: ScenarioGraphNodeType
}

export interface InfraAttackNode extends BaseGraphNode {
  type: ScenarioGraphNodeType.InfraAttack
  target_definition?: TargetDefinition
  impact_definition?: ImpactDefinition
  attack_configuration?: AttackConfiguration
  include_new_targets?: boolean
  continue_on_empty_targets?: boolean
  state?: SnakeCasedProperties<InfraAttackNodeState>
}

interface FailureFlagInputForScenario {
  app: FailureFlagSelector
  duration: string
  effect: ExperimentEffect
  flag: FailureFlagSelector
  rate: number
  staticTargeting: boolean
}

export interface FailureFlagNode extends BaseGraphNode {
  failureFlagInputForScenario: FailureFlagInputForScenario
  state?: FailureFlagNodeState
  type: ScenarioGraphNodeType.FailureFlag
}

export interface StatusCheckNode extends BaseGraphNode {
  name: string
  description?: string
  autoGeneratedStatusCheckId?: string
  thirdPartyPresets?: string
  endpointConfiguration: EndpointConfiguration
  evaluationConfiguration: EvaluationConfiguration
  statusCheckId: string
  statusCheckReferenceId?: string
  isPrivateNetwork?: boolean
  payload?: string
  state?: SnakeCasedProperties<StatusCheckNodeState>
  isAutoGenerated?: boolean
}

export interface ContinuousStatusCheckNode extends StatusCheckNode {
  type: ScenarioGraphNodeType.ContinuousStatusCheck
  intervalInSeconds?: number
}

export interface SynchronousStatusCheckNode extends StatusCheckNode {
  type: ScenarioGraphNodeType.SynchronousStatusCheck
  isHaltable?: boolean
}

export interface ConcurrentNode extends BaseGraphNode {
  branches: ScenarioGraph[]
  type: ScenarioGraphNodeType.Concurrent
  state?: SnakeCasedProperties<BaseNodeState>
}

export interface DelayNode extends BaseGraphNode {
  delay: number
  type: ScenarioGraphNodeType.Delay
  state?: SnakeCasedProperties<BaseNodeState>
}

export type GraphNode =
  | InfraAttackNode
  | FailureFlagNode
  | DelayNode
  | ConcurrentNode
  | SynchronousStatusCheckNode

export interface GraphNodeList {
  [key: string]: GraphNode
}

export interface BaseGraph {
  observers?: ContinuousStatusCheckNode[]
  nodes: GraphNodeList
  start_id: string
}

export type ScenarioGraph = SnakeCasedProperties<BaseGraph> &
  CamelCasedProperties<BaseGraph>

export interface MultiSelect {
  [key: string]: string[]
}

export interface EndpointConfiguration {
  url: string
  headers: Headers
}

export type Headers = Record<string, string>

export interface EvaluationConfiguration {
  okLatencyMaxMs: number
  okStatusCodes: string[]
  responseBodyEvaluation: ResponseBodyEvaluation | null
}

export interface ImpactDefinition {
  infra_command_type: string
  infra_command_args: SnakeCaseAttackArgs
}

export interface TargetDefinition {
  strategy_type: string
  target_type: ProvenanceTargetType
  strategy: Strategy
}

export interface Strategy {
  percentage: number
  all_hosts?: boolean
  attrs: Attrs
  type: string
}

export interface Attrs {
  multiSelectLabels?: MultiSelect
  multiSelectTags?: MultiSelect
}

export interface StepAttack {
  attackType: 'ILFI'
  attackConfiguration: AttackConfiguration
  targetDefinition: TargetDefinition
  impactDefinition: ImpactDefinition
  includeNewTargets?: boolean
  continueOnEmptyTargets?: boolean
}

export interface BaseScenario {
  guid: string
  name: string
  description?: string
  // string makes these friendlier when dealing with JSON imports in tests, etc.
  created_at: Date | string
  updated_at: Date | string
  graph: ScenarioGraph
}

export interface Scenario extends BaseScenario {
  org_id: string
  hypothesis?: string
  state: string
  create_source: string
  created_by: string
  edit_counter?: number
  last_run_at: Date | string
  multi_select_tags?: {
    [tagName: string]: string[]
  }
  multi_select_labels?: {
    [labelName: string]: string[]
  }
  created_from_type?: string
  created_from_id?: string
  steps?: Record<string, any>
  recommended_scenario_id?: string
}

export interface RecommendedScenario extends BaseScenario {
  guid: string
  priority: number
  hypothesis: string
  static_endpoint_name: string
  multi_select_tags: MultiSelect
}

export interface SharedScenario extends BaseScenario {
  sharedByTeam: string
  hypothesis: string
  sharedBy: string
  sharedFromScenario: string
  createSource: string
  createdAt: string
  updatedAt: string
  recommendedScenarioId: string
  multiSelectTags: MultiSelect
}

export interface ServiceTarget {
  serviceId?: string
  targetingStrategy: TargetingStrategy
}
